import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;
const fr = (d) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '—');

/** Factures & Reçus ESS : dossiers validés sans encaissement, montant à réclamer (suivi interne). */
export default function PaiementsAReclamer() {
  const [data, setData] = useState(null);
  const [envoi, setEnvoi] = useState(null);
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });
  const charger = () => axios.get(`${API}/souscriptions/admin/a-reclamer`, { headers: headers() })
    .then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, montant_total: 0 }));
  useEffect(() => { charger(); }, []);
  const relancer = async (x) => {
    if (!window.confirm(`Envoyer au membre un rappel de ${eur(x.reste)} par e-mail et SMS, avec le lien de paiement carte ?`)) return;
    setEnvoi(x.souscription_id);
    try {
      const { data: r } = await axios.post(`${API}/souscriptions/admin/${x.souscription_id}/relance-a-reclamer`, {}, { headers: headers() });
      const canaux = [r.relance.email && 'e-mail', r.relance.sms && 'SMS'].filter(Boolean).join(' + ') || 'aucun canal';
      toast.success(`Rappel envoyé (${canaux})`);
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Envoi impossible');
    } finally { setEnvoi(null); }
  };
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-4" data-testid="paiements-a-reclamer">
      <div>
        <h2 className="text-lg font-bold text-[#0a3d62]">Paiements à réclamer</h2>
        <p className="text-sm text-slate-500">
          Dossiers validés par le Super Admin sans encaissement : contrat actif, montant restant dû.
          <b className="ml-1 text-[#0a3d62]" data-testid="a-reclamer-total">{data.total} dossier(s) · {eur(data.montant_total)}</b>
        </p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr><th className="p-2">Membre</th><th className="p-2">Contrat</th><th className="p-2">À réclamer</th><th className="p-2">Reste dû</th>
              <th className="p-2">Date limite</th><th className="p-2">Motif</th><th className="p-2">Validé par</th><th className="p-2">Relances</th></tr>
          </thead>
          <tbody>
            {data.items.map((x) => (
              <tr key={x.souscription_id} className={`border-t border-slate-100 ${x.en_retard ? 'bg-rose-50/60' : ''}`} data-testid={`a-reclamer-${x.souscription_id}`}>
                <td className="p-2 font-semibold">{x.raison_sociale || '—'}</td>
                <td className="p-2 font-mono text-xs">{x.numero_police}</td>
                <td className="p-2">{eur(x.montant)}</td>
                <td className="p-2 font-bold text-[#0a3d62]">{eur(x.reste)}{x.encaisse_depuis > 0 && <div className="text-[10px] font-normal text-emerald-700">{eur(x.encaisse_depuis)} encaissés depuis</div>}</td>
                <td className={`whitespace-nowrap p-2 ${x.en_retard ? 'font-semibold text-rose-700' : ''}`}>
                  {x.en_retard && <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />}{fr(x.date_limite)}
                </td>
                <td className="p-2 text-xs">{x.motif}</td>
                <td className="p-2 text-xs text-slate-500">{x.par}<div>{fr(x.at)}</div></td>
                <td className="p-2 text-xs">
                  <div data-testid={`a-reclamer-relances-${x.souscription_id}`} className="mb-1 text-slate-500">
                    {x.nb_relances ? <>{x.nb_relances} relance(s)<div>dernière : {fr(x.derniere_relance)}</div></> : 'Aucune relance'}
                  </div>
                  <button type="button" onClick={() => relancer(x)} disabled={envoi === x.souscription_id || x.reste < 0.5}
                    data-testid={`a-reclamer-relancer-${x.souscription_id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-[#0a3d62] px-3 py-1 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50">
                    {envoi === x.souscription_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}Relancer
                  </button>
                </td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={8} className="p-6 text-center text-slate-400">Aucun paiement à réclamer</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">Le dossier sort de cette liste quand le paiement est validé, soldé par paiements partiels ou réglé par carte via le lien de relance.</p>
    </div>
  );
}
